import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import React, { SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import authSlice from "../../store/slices/auth.slice";
import { RootState } from "../../store/store";
import { ETheme } from "../enums/user/ETheme";
import { ILayout } from "../interfaces/shared/ILayout";
import { lightPalette } from "../mui/palettes";
import { darkTheme, lightTheme } from "../mui/theme";

interface IProps {
  isCollapsed: boolean;
  routes: ILayout[] | undefined;

  setIsCollapsed: React.Dispatch<SetStateAction<boolean>>;
}

const Header = ({ isCollapsed, setIsCollapsed, routes }: IProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const company = useSelector((state: RootState) => state?.companySettings);
  const name = userData?.firstName + " " + userData?.lastName;
  const dispatch = useDispatch();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logoutUser = () => {
    localStorage.clear();
    navigate("/");
    dispatch(authSlice.actions.setLogoutState());
  };
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md")); // Hides sidebar on md and below

  return (
    <header>
      {isMobileOrTablet ? (
        <Box
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            localStorage.setItem("menuCollapsed", String(!isCollapsed));
          }}
        >
          <MenuIcon fontSize="large" />
          <Drawer open={isCollapsed} onClose={() => setIsCollapsed(false)}>
            <Box
              sx={{
                width: 250,
                height: "100%",
                backgroundColor:
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.background.default
                    : darkTheme.palette.background.default,
              }}
              role="presentation"
              onClick={() => setIsCollapsed(false)}
            >
              <img
                src={
                  company?.thumbnail
                    ? userData?.theme === ETheme.DARK
                      ? company?.invertedThumbnail
                      : company?.thumbnail
                    : userData?.theme === "light"
                      ? "/logo-light.png"
                      : "/logo-dark.svg"
                }
                alt="Logo"
                width="50%"
                style={{ padding: "1rem" }}
              />
              <List>
                {routes?.map(({ title, path, icon }) => {
                  const isActive = pathname?.includes(path);
                  return (
                    <ListItem key={title} disablePadding>
                      <ListItemButton onClick={() => navigate(path)}>
                        <Typography
                          sx={{
                            color: isActive ? "var(--primaryColor)" : "",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ marginRight: 10, display: "flex" }}>
                            {icon}
                          </span>
                          {t(title)}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </Box>
      ) : (
        <h2>
          {t("general.login.welcome")}, {name} 👋
        </h2>
      )}
      <div className={"d-flex align-items-center"}>
        <Box sx={{ marginRight: "20px", cursor: "pointer" }}>
          <Card sx={{ padding: "18px", display: "flex" }}>
            {userData && userData?.unreadNotifications > 0 ? (
              <Badge
                badgeContent={userData.unreadNotifications}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    color: lightPalette.secondary.main,
                  },
                }}
              >
                <NotificationsActiveOutlinedIcon />
              </Badge>
            ) : (
              <NotificationsActiveOutlinedIcon />
            )}
          </Card>
        </Box>
        <Card sx={{ padding: "10px", cursor: "pointer" }}>
          <Box sx={{ display: "flex" }} onClick={handleOpenUserMenu}>
            <IconButton sx={{ p: 0, marginRight: "0.5rem" }}>
              <Avatar
                alt={name}
                src={`${userData?.profilePhoto?.thumbnail}?v=${userData?.profilePhoto?.updatedAt}`}
              />
            </IconButton>
            <div>
              <h4 className={"my-0"}>{name}</h4>
              <span
                style={{ color: darkTheme.palette.info.main, fontSize: 12 }}
              >
                {t(`roles.${userData?.role}`)}
              </span>
            </div>
            <KeyboardArrowDownOutlinedIcon />
          </Box>
          <Menu
            sx={{ mt: "60px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem
              onClick={() => {
                navigate(`/user/update/${userData?.id}`);
                setAnchorElUser(null);
              }}
            >
              <Typography sx={{ display: "flex", alignItems: "center" }}>
                <Person2OutlinedIcon />
                {t("header.myProfile")}
              </Typography>
            </MenuItem>
            <MenuItem onClick={logoutUser}>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "error.main",
                }}
              >
                <LogoutOutlinedIcon />
                {t("header.logout")}
              </Typography>
            </MenuItem>
          </Menu>
        </Card>
      </div>
    </header>
  );
};

export default Header;
